import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react'
import { YardAlert, apiClient } from '@nyos/shared'

interface AlertContextType {
  alerts: Map<string, YardAlert>
  addAlert: (alert: YardAlert) => void
  resolveAlert: (assetId: string) => void
  clearAlerts: () => void
  fetchActiveAlerts: (facilityId: string) => Promise<void>
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [alerts, setAlerts] = useState<Map<string, YardAlert>>(new Map())

  const addAlert = useCallback((alert: YardAlert) => {
    setAlerts(prev => new Map(prev).set(alert.relatedEntityId || '', alert))
  }, [])

  const resolveAlert = useCallback((assetId: string) => {
    setAlerts(prev => {
      const newMap = new Map(prev)
      const alert = newMap.get(assetId)
      if (alert) {
        newMap.delete(assetId)
      }
      return newMap
    })
  }, [])

  const clearAlerts = useCallback(() => {
    setAlerts(new Map())
  }, [])

  const fetchActiveAlerts = useCallback(async (facilityId: string) => {
    try {
      const data: YardAlert[] = await apiClient.get(`/api/v1/yards/${facilityId}/alerts`)
      if (data) {
        const alertMap = new Map<string, YardAlert>()
        data.forEach(alert => {
          if (!alert.resolved && alert.relatedEntityId) {
            alertMap.set(alert.relatedEntityId, alert)
          }
        })
        setAlerts(alertMap)
      }
    } catch (error) {
      console.error('Failed to fetch active alerts:', error)
    }
  }, [])

  const value = useMemo(() => ({ alerts, addAlert, resolveAlert, clearAlerts, fetchActiveAlerts }), [alerts, addAlert, resolveAlert, clearAlerts, fetchActiveAlerts])

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  )
}

export const useAlerts = () => {
  const context = useContext(AlertContext)
  if (context === undefined) {
    throw new Error('useAlerts must be used within an AlertProvider')
  }
  return context
}
